import type {IntlShape, MessageDescriptor} from 'react-intl';
import type {CallAiError} from '../../../store/features/calls/callsService';
import {insightsMessages as m} from './insightsMessages';

export const errorStatus = (error: unknown): number | string | null => {
    if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as {status: unknown}).status;
        if (typeof status === 'number' || typeof status === 'string') return status;
    }
    return null;
};

export const CONTENT_FILTERED_CODE = 'AI_CONTENT_FILTERED';

export const isContentFiltered = (error: unknown): boolean => {
    if (errorStatus(error) !== 422) return false;
    const data = (error as {data?: unknown}).data;
    return Boolean(data && typeof data === 'object' && (data as {code?: unknown}).code === CONTENT_FILTERED_CODE);
};

export const retryAfterMinutes = (error: unknown): number | null => {
    const seconds = (error as CallAiError | null)?.retryAfterSeconds;
    return seconds && seconds > 0 ? Math.max(1, Math.ceil(seconds / 60)) : null;
};

export const isRateLimited = (error: unknown): boolean => errorStatus(error) === 429;

export interface RateLimitMessages {
    withMinutes: MessageDescriptor;
    soon: MessageDescriptor;
}

const defaultRateLimitMessages: RateLimitMessages = {withMinutes: m.rateLimited, soon: m.rateLimitedSoon};

export const rateLimitMessage = (intl: IntlShape, error: unknown, messages: RateLimitMessages = defaultRateLimitMessages): string => {
    const minutes = retryAfterMinutes(error);
    return minutes ? intl.formatMessage(messages.withMinutes, {minutes}) : intl.formatMessage(messages.soon);
};

export const generationErrorMessage = (intl: IntlShape, error: unknown, fallback: MessageDescriptor = m.summaryFailed): string => {
    if (isContentFiltered(error)) return intl.formatMessage(m.contentFiltered);
    const status = errorStatus(error);
    if (status === 429) return rateLimitMessage(intl, error);
    if (status === 503) return intl.formatMessage(m.summaryUnavailable);
    if (status === 410) return intl.formatMessage(m.summaryExpired);
    if (status === 409) return intl.formatMessage(m.nothingToWorkFrom);
    return intl.formatMessage(fallback);
};

export const assistantErrorMessage = (
    intl: IntlShape,
    error: unknown,
    notFound: MessageDescriptor,
    fallback: MessageDescriptor,
    rateLimit: RateLimitMessages = defaultRateLimitMessages,
): string => {
    if (isContentFiltered(error)) return intl.formatMessage(m.contentFiltered);
    const status = errorStatus(error);
    if (status === 429) return rateLimitMessage(intl, error, rateLimit);
    if (status === 503) return intl.formatMessage(m.summaryUnavailable);
    if (status === 404) return intl.formatMessage(notFound);
    return intl.formatMessage(fallback);
};

export const isFinalError = (error: unknown): boolean => {
    if (isContentFiltered(error)) return true;
    const status = errorStatus(error);
    return status === 404 || status === 409 || status === 410;
};

export const canRetry = (error: unknown): boolean => !isFinalError(error) && errorStatus(error) !== 429;
